import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'access-paycircle',
  version: '1.0.0',
  properties: [
    {
      key: 'credentialId',
      label: 'PayCircle Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'paycircle',
      },
      description: 'Select your PayCircle API credential',
    },
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'company',
      options: [
        { value: 'company', name: 'Company' },
        { value: 'teamMember', name: 'Team Member' },
        { value: 'payrollPeriod', name: 'Payroll Period' },
        { value: 'payment', name: 'Payment' },
        { value: 'webhook', name: 'Webhook' },
      ],
      description: 'The PayCircle resource to interact with',
    },

    // ===== COMPANY ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getCompanyList',
      options: [
        { value: 'getCompanyList', name: 'Get Company List' },
        { value: 'getCompany', name: 'Get Company' },
      ],
      displayOptions: {
        show: {
          resource: ['company'],
        },
      },
    },

    // ===== TEAM MEMBER ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getTeamMembers',
      options: [
        { value: 'getTeamMembers', name: 'Get Team Members' },
        { value: 'getTeamMember', name: 'Get Team Member' },
        { value: 'createTeamMember', name: 'Create Team Member' },
        { value: 'updateTeamMember', name: 'Update Team Member' },
      ],
      displayOptions: {
        show: {
          resource: ['teamMember'],
        },
      },
    },

    // ===== PAYROLL PERIOD ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getPayrollPeriods',
      options: [
        { value: 'getPayrollPeriods', name: 'Get Payroll Periods' },
        { value: 'getPayrollPeriod', name: 'Get Payroll Period' },
        { value: 'getCurrentPeriod', name: 'Get Current Period' },
      ],
      displayOptions: {
        show: {
          resource: ['payrollPeriod'],
        },
      },
    },

    // ===== PAYMENT ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getPayments',
      options: [
        { value: 'getPayments', name: 'Get Payments' },
        { value: 'getPayment', name: 'Get Payment' },
        { value: 'exportPayments', name: 'Export Payments' },
      ],
      displayOptions: {
        show: {
          resource: ['payment'],
        },
      },
    },

    // ===== WEBHOOK ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'listWebhooks',
      options: [
        { value: 'listWebhooks', name: 'List Webhooks' },
        { value: 'createWebhook', name: 'Create Webhook' },
        { value: 'deleteWebhook', name: 'Delete Webhook' },
      ],
      displayOptions: {
        show: {
          resource: ['webhook'],
        },
      },
    },

    // ===== COMPANY FIELDS =====
    {
      key: 'companyId',
      label: 'Company ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The company ID/GUID',
      displayOptions: {
        show: {
          resource: ['company'],
          action: ['getCompany'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getCompanies',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ===== TEAM MEMBER FIELDS =====
    {
      key: 'companyId',
      label: 'Company ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The company ID',
      displayOptions: {
        show: {
          resource: ['teamMember'],
          action: ['getTeamMembers', 'createTeamMember'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getCompanies',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'teamMemberId',
      label: 'Team Member ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The team member ID/GUID',
      displayOptions: {
        show: {
          resource: ['teamMember'],
          action: ['getTeamMember', 'updateTeamMember'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getTeamMembers',
          dependsOn: ['companyId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'teamMemberData',
      label: 'Team Member Data',
      type: 'json',
      required: true,
      placeholder: '{\n  "firstName": "John",\n  "lastName": "Smith",\n  "email": "john.smith@example.com"\n}',
      description: 'Team member data as JSON object',
      displayOptions: {
        show: {
          resource: ['teamMember'],
          action: ['createTeamMember', 'updateTeamMember'],
        },
      },
    },

    // ===== PAYROLL PERIOD FIELDS =====
    {
      key: 'companyId',
      label: 'Company ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The company ID',
      displayOptions: {
        show: {
          resource: ['payrollPeriod'],
          action: ['getPayrollPeriods', 'getCurrentPeriod'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getCompanies',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'taxYear',
      label: 'Tax Year',
      type: 'string',
      required: false,
      placeholder: 'e.g., 2025/26',
      description: 'Filter by tax year (optional)',
      displayOptions: {
        show: {
          resource: ['payrollPeriod'],
          action: ['getPayrollPeriods'],
        },
      },
    },
    {
      key: 'periodId',
      label: 'Period ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The payroll period ID',
      displayOptions: {
        show: {
          resource: ['payrollPeriod'],
          action: ['getPayrollPeriod'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getPayrollPeriods',
          dependsOn: ['companyId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ===== PAYMENT FIELDS =====
    {
      key: 'periodId',
      label: 'Period ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The payroll period ID',
      displayOptions: {
        show: {
          resource: ['payment'],
          action: ['getPayments', 'exportPayments'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getPayrollPeriods',
          dependsOn: ['companyId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'paymentId',
      label: 'Payment ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The payment record ID',
      displayOptions: {
        show: {
          resource: ['payment'],
          action: ['getPayment'],
        },
      },
    },
    {
      key: 'format',
      label: 'Export Format',
      type: 'options',
      required: false,
      default: 'json',
      options: [
        { value: 'json', name: 'JSON' },
        { value: 'csv', name: 'CSV' },
      ],
      description: 'Export file format',
      displayOptions: {
        show: {
          resource: ['payment'],
          action: ['exportPayments'],
        },
      },
    },

    // ===== WEBHOOK FIELDS =====
    {
      key: 'url',
      label: 'Webhook URL',
      type: 'string',
      required: true,
      placeholder: 'https://your-server.com/webhook',
      description: 'The callback URL for webhook events',
      displayOptions: {
        show: {
          resource: ['webhook'],
          action: ['createWebhook'],
        },
      },
    },
    {
      key: 'events',
      label: 'Events',
      type: 'multiOptions',
      required: true,
      options: [
        { value: 'payroll.started', name: 'Payroll Started' },
        { value: 'payroll.completed', name: 'Payroll Completed' },
        { value: 'payroll.approved', name: 'Payroll Approved' },
        { value: 'payment.created', name: 'Payment Created' },
        { value: 'payment.updated', name: 'Payment Updated' },
        { value: 'teamMember.created', name: 'Team Member Created' },
        { value: 'teamMember.updated', name: 'Team Member Updated' },
        { value: 'teamMember.terminated', name: 'Team Member Terminated' },
      ],
      description: 'Events to subscribe to',
      displayOptions: {
        show: {
          resource: ['webhook'],
          action: ['createWebhook'],
        },
      },
    },
    {
      key: 'secret',
      label: 'Webhook Secret',
      type: 'string',
      required: false,
      placeholder: 'Optional secret for signature validation',
      description: 'Secret key for webhook signature validation (optional)',
      displayOptions: {
        show: {
          resource: ['webhook'],
          action: ['createWebhook'],
        },
      },
    },
    {
      key: 'webhookId',
      label: 'Webhook ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The webhook ID to delete',
      displayOptions: {
        show: {
          resource: ['webhook'],
          action: ['deleteWebhook'],
        },
      },
    },

    // ===== PAGINATION FIELDS =====
    {
      key: 'page',
      label: 'Page',
      type: 'number',
      required: false,
      default: 1,
      placeholder: '1',
      description: 'Page number for paginated results',
      displayOptions: {
        show: {
          resource: ['teamMember', 'payment'],
          action: ['getTeamMembers', 'getPayments'],
        },
      },
    },
    {
      key: 'pageSize',
      label: 'Page Size',
      type: 'number',
      required: false,
      default: 25,
      placeholder: '25',
      description: 'Number of results per page (max 1000)',
      displayOptions: {
        show: {
          resource: ['teamMember', 'payment'],
          action: ['getTeamMembers', 'getPayments'],
        },
      },
    },
  ],
};

export default schema;
